// @ts-nocheck
import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // ✅ ページ読み込み時に最大購入数チェック実行
    this.#initializeMaxQtyMessages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * ✅ 初期ロード時、全商品に対して上限チェック
   */
  #initializeMaxQtyMessages() {
    const selectors = this.refs.quantitySelectors || [];
    selectors.forEach((selector, i) => {
      const input = selector?.querySelector('input[data-maximum-quantity]');
      const maxQty = parseInt(input?.dataset.maximumQuantity || '0', 10);
      const qty = parseInt(input?.value || '0', 10);

      if (maxQty && qty > maxQty) {
        input.value = maxQty;
        const err = this.refs[`cartItemError-${i + 1}`];
        const container = this.refs[`cartItemErrorContainer-${i + 1}`];
        if (err && container) {
          err.textContent = `この商品はお一人様 ${maxQty} 個までです。`;
          container.classList.remove('hidden');
        }
        this.updateQuantity({ line: i + 1, quantity: maxQty, action: 'change' });
      }
    });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const input = quantitySelector?.querySelector('input[data-maximum-quantity]');
    const maxQty = parseInt(input?.dataset.maximumQuantity || '0', 10);
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (maxQty && quantity > maxQty) {
      input.value = maxQty;
      if (cartItemError && cartItemErrorContainer) {
        cartItemError.textContent = `この商品はお一人様 ${maxQty} 個までです。`;
        cartItemErrorContainer.classList.remove('hidden');
      }
      this.updateQuantity({ line, quantity: maxQty, action: 'change' });
      return;
    }

    if (quantity === 0) return this.onLineItemRemove(line);

    this.updateQuantity({ line, quantity, action: 'change' });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = lineItemRow?.querySelector('text-component');
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;
    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // ✅ morph後にもエラー状態を再適用（上限エラーを消さない）
        setTimeout(() => this.#restoreErrorMessages(), 100);
      })
      .catch(console.error)
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * ✅ morph後に再適用
   */
  #restoreErrorMessages() {
    const selectors = this.refs.quantitySelectors || [];
    selectors.forEach((selector, i) => {
      const input = selector?.querySelector('input[data-maximum-quantity]');
      const maxQty = parseInt(input?.dataset.maximumQuantity || '0', 10);
      const qty = parseInt(input?.value || '0', 10);
      if (maxQty && qty >= maxQty) {
        const err = this.refs[`cartItemError-${i + 1}`];
        const container = this.refs[`cartItemErrorContainer-${i + 1}`];
        if (err && container) {
          err.textContent = `この商品はお一人様 ${maxQty} 個までです。`;
          container.classList.remove('hidden');
        }
      }
    });
  }

  handleDiscountUpdate = (event) => this.#handleCartUpdate(event);

  #handleCartError = (line, parsedResponseText) => {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemError && cartItemErrorContainer)) return;

    const errorMsg = parsedResponseText.errors || parsedResponseText.message || '在庫数を超えています。';
    cartItemError.textContent = errorMsg;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
